import React, { useState } from 'react';
import MakeGableIcon from '@mui/icons-material/AlignVerticalBottom';
import StubTrussIcon from '@mui/icons-material/ContentCut';
import EditIcon from '@mui/icons-material/Edit';
import RewebIcon from '@mui/icons-material/Insights';
import ConvertToSettingsIcon from '@mui/icons-material/SettingsBackupRestore';
import SouthIcon from '@mui/icons-material/South';
import ReplaceMaterialsIcon from '@mui/icons-material/SwapHoriz';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import { isFeaturePatternTruss, isProfileTruss } from '@paragon/contract-library';
import { hasFreeSubscription, hasTrussDesignPermission, UpgradeMenuItems } from '@paragon/login-tools';
import { WalkthroughBlurbAnchor } from '@paragon/tour-guide';

import { useAppMonitor } from '../../../monitor';
import { showReplaceMaterialsPanel } from '../../../store/actions/appActions';
import { startRewebTruss } from '../../../store/actions/editComponentActions';
import { startDropTopChords } from '../../../store/actions/insertActions';
import { showEditDepthDialog, showUpgradeAccountDialog } from '../../../store/actions/popupActions';
import { selectItem } from '../../../store/actions/selectActions';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import { isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import { inViewOnlyMode, propertiesShouldBeViewOnly } from '../../../store/selectors/viewOnlyModeSelectors';
import { setTrussEditsPanel } from '../../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useConvertToSettingsLauncher } from '../../misc/useConvertToSettingsLauncher';
import { ToolbarIconButton } from '../top/ToolbarIconButton';

export function EditTrussButton() {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const inLayoutMode = useAppSelector(isLayoutViewActive);
  const isViewOnly = useAppSelector(
    (state) => inViewOnlyMode(state) || !hasTrussDesignPermission(state) || propertiesShouldBeViewOnly(state),
  );
  const isFreeUser = useAppSelector(hasFreeSubscription);
  const componentDesign = useAppSelector(getActiveComponentDesign);

  const [renderConvertToSettingsDialog, launchConvertToSettings] = useConvertToSettingsLauncher();

  if (inLayoutMode || componentDesign == null || isViewOnly) return null;

  return (
    <>
      <WalkthroughBlurbAnchor id="edit-truss-button" blurbPlacement="bottom">
        <ToolbarIconButton
          title="Edit Truss"
          icon={<EditIcon />}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
          }}
        />
      </WalkthroughBlurbAnchor>

      {anchorEl != null && (
        <Menu open anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          <UpgradeMenuItems
            active={isFreeUser}
            briefDescription="access these features"
            onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
          >
            {isFeaturePatternTruss(componentDesign.component) && (
              <MenuItem
                disabled={isFreeUser}
                onClick={() => {
                  setAnchorEl(null);
                  dispatch(setTrussEditsPanel('stub'));

                  monitor.recordInteraction('Edit Truss => Stub Truss clicked');
                }}
              >
                <ListItemIcon>
                  <StubTrussIcon />
                </ListItemIcon>
                <ListItemText>Stub Truss</ListItemText>
              </MenuItem>
            )}

            {(isProfileTruss(componentDesign.component) ||
              (isFeaturePatternTruss(componentDesign.component) && componentDesign.component.settings)) && (
              <MenuItem
                disabled={isFreeUser}
                onClick={() => {
                  setAnchorEl(null);

                  if (isProfileTruss(componentDesign.component)) {
                    dispatch(startRewebTruss());
                  } else {
                    dispatch(setTrussEditsPanel('reweb-zone'));
                  }

                  monitor.recordInteraction('Edit Truss => Reweb Truss clicked');
                }}
              >
                <ListItemIcon>
                  <RewebIcon />
                </ListItemIcon>
                <ListItemText>Reweb Truss</ListItemText>
              </MenuItem>
            )}

            <Divider />

            <MenuItem
              disabled={isFreeUser}
              onClick={() => {
                setAnchorEl(null);
                dispatch(setTrussEditsPanel('span-macros'));

                monitor.recordInteraction('Edit Truss => Adjust Span clicked');
              }}
            >
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              <ListItemText>Adjust Span</ListItemText>
            </MenuItem>

            {isProfileTruss(componentDesign.component) && (
              <>
                <MenuItem
                  disabled={isFreeUser}
                  onClick={() => {
                    setAnchorEl(null);
                    dispatch(setTrussEditsPanel('overhang-macros'));

                    monitor.recordInteraction('Edit Truss => Adjust Overhang clicked');
                  }}
                >
                  <ListItemIcon>
                    <EditIcon />
                  </ListItemIcon>
                  <ListItemText>Adjust Overhang</ListItemText>
                </MenuItem>

                <MenuItem
                  disabled={isFreeUser}
                  onClick={() => {
                    setAnchorEl(null);
                    dispatch(setTrussEditsPanel('heel-macros'));

                    monitor.recordInteraction('Edit Truss => Adjust Heels clicked');
                  }}
                >
                  <ListItemIcon>
                    <EditIcon />
                  </ListItemIcon>
                  <ListItemText>Adjust Heels</ListItemText>
                </MenuItem>
              </>
            )}

            {isFeaturePatternTruss(componentDesign.component) && (
              <MenuItem
                disabled={isFreeUser || componentDesign.component.inferredSettings == null}
                onClick={() => {
                  setAnchorEl(null);
                  dispatch(showEditDepthDialog());

                  monitor.recordInteraction('Edit Truss => Adjust Depth clicked');
                }}
              >
                <ListItemIcon>
                  <EditIcon />
                </ListItemIcon>
                <ListItemText>Adjust Depth</ListItemText>
              </MenuItem>
            )}
          </UpgradeMenuItems>

          <Divider />

          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              dispatch(startDropTopChords());

              monitor.recordInteraction('Edit Truss => Drop Flat Top Chords clicked');
            }}
          >
            <ListItemIcon>
              <SouthIcon />
            </ListItemIcon>
            <ListItemText>Drop Flat Top Chords</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              dispatch(showReplaceMaterialsPanel(componentDesign.guid, 'lumbers'));

              monitor.recordInteraction('Edit Truss => Replace Lumber clicked');
            }}
          >
            <ListItemIcon>
              <ReplaceMaterialsIcon />
            </ListItemIcon>
            <ListItemText>Replace Lumber</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              launchConvertToSettings();
            }}
          >
            <ListItemIcon>
              <ConvertToSettingsIcon />
            </ListItemIcon>
            <ListItemText>Convert to Settings</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              setAnchorEl(null);

              dispatch(selectItem(componentDesign, 'componentDesign'));
              dispatch(setTrussEditsPanel('gable'));

              monitor.recordInteraction('Edit Truss => Make Gable clicked');
            }}
          >
            <ListItemIcon>
              <MakeGableIcon />
            </ListItemIcon>
            <ListItemText>Make Gable</ListItemText>
          </MenuItem>
        </Menu>
      )}

      {renderConvertToSettingsDialog()}
    </>
  );
}
